// In local dev VITE_API_URL is typically unset and the Vite dev-server proxies
// "/api" and "/health" to the .NET backend (see vite.config.ts). In production
// the frontend and backend are separate Cloud Run services, so VITE_API_URL is
// baked in at build time to point at the backend service URL.
import type { CalcInputs, ProjectionSummary, YearRow } from "./atomika-calc";
import type {
  CustomerListResult,
  DashboardSavingsData,
  DashboardSeries,
  DashboardSourcesData,
  ShowcaseEntry,
  ShowcaseTech,
  DashboardStatusData,
  FleetData,
} from "./atomika-dashboard";
import type { OverviewData } from "./atomika-overview";
import type {
  ActivateCustomerPayload,
  Activity,
  Company,
  CompanyBuilding,
  CompanyDetail,
  CompanySearchResult,
  RegisterCompanyPayload,
  SaveBuildingPayload,
  SaveBuildingResult,
  CustomerInvoices,
  InvoiceMonthValue,
  WaterTariffs,
  ExpectedSaving,
  SavingsShare,
  ServiceTermsBaseline,
  ServiceTermsKind,
  ServiceTermsList,
  ServiceTermsVersion,
  Contact,
  CreateActivityPayload,
  CreateContactPayload,
  CreateContractPayload,
  CreateLaufidCustomerPayload,
  CreateOpportunityPayload,
  Contract,
  CrmDocument,
  DocumentKind,
  LaufidCustomerResult,
  LaufidLink,
  LaufidStatus,
  MarkadurCustomerListResult,
  Opportunity,
  Pipeline,
  StagesConfig,
  UpdateContactPayload,
  UpdateContractPayload,
  UpdateOpportunityPayload,
} from "./markadur";
import type { DutyPerson, DutyShift } from "./vaktaskra";

export interface AdminSettings {
  recipientUserIds: string[];
  rollingWindow: number;
  tariffPerM3: number;
  bands: FravikBands;
  smtp: {
    host: string | null;
    port: number;
    useSsl: boolean;
    user: string | null;
    from: string | null;
    webBaseUrl: string | null;
    passwordSet: boolean;
    configured: boolean;
  };
  vakt: {
    baseUrl: string;
    auth: VaktAuth;
    username: string | null;
    passwordSet: boolean;
    tokenSet: boolean;
    configured: boolean;
    keyMap: Record<string, string>;
  };
  laufid: LaufidConnectionSettings;
}

export interface LaufidConnectionSettings {
  partnerSlug: string;
  baseUrl: string;
  appUrl: string;
  tokenSet: boolean;
  configured: boolean;
}

export interface UpdateSettingsPayload {
  recipientUserIds?: string[];
  rollingWindow?: number;
  tariffPerM3?: number;
  bands?: FravikBands;
  smtp?: {
    host?: string;
    port?: number;
    useSsl?: boolean;
    user?: string;
    password?: string;
    from?: string;
    webBaseUrl?: string;
  };
  vakt?: {
    baseUrl?: string;
    auth?: VaktAuth;
    username?: string;
    password?: string;
    token?: string;
    keyMap?: Record<string, string>;
  };
  laufid?: LaufidConnectionPatch;
}

export interface LaufidConnectionPatch {
  partnerSlug?: string;
  baseUrl?: string;
  appUrl?: string;
  token?: string;
}

export type ApiKeyScope = "ingest" | "read" | "admin" | "customer-read";

export interface ApiKeySummary {
  id: number;
  name: string;
  ownerOrg: string;
  prefix: string;
  scopes: string;
  customerId: number | null;
  customerKennitala: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  active: boolean;
}

export interface ApiKeyCreated extends Omit<
  ApiKeySummary,
  "lastUsedAt" | "revokedAt" | "active"
> {
  key: string;
}

export interface CreateApiKeyPayload {
  name: string;
  ownerOrg: string;
  scopes: string;
  expiresAt?: string | null;
}

export type VaktAuth = "login" | "token";

export interface VaktValveOrientation {
  site: string;
  valve: string;
  samples: number;
  supplyC: number | null;
  returnC: number | null;
  flowLps: number | null;
  inverted: boolean;
}

export interface VaktTestResult {
  ok: boolean;
  error?: string;
  deviceCount?: number;
  devices?: { name: string; active: boolean }[];
  sampleDevice?: string | null;
  sampleKeys?: string[];
  orientation?: VaktValveOrientation[];
}

export interface AtomikaBuilding {
  siteId: string;
  name: string;
  lokarCount: number;
  kennitala: string | null;
  customerName: string | null;
  status: "green" | "yellow" | "red" | null;
  lastSyncAt: string | null;
  dataSource: string;
}

export interface AtomikaDeviceCustomer {
  kennitala: string;
  name: string;
}

export interface FleetEarningsMonth {
  month: string;
  savedIsk: number;
  atomikaIsk: number;
  customerIsk: number;
}

export interface FleetSummary {
  counts: { customers: number; buildings: number; valves: number };
  health: { green: number; amber: number; red: number; noData: number };
  totalSavedM3: number;
  totalSavedIsk: number;
  atomikaEarningsIsk: number;
  earnings: FleetEarningsMonth[];
}

export interface FravikBand {
  redLo: number;
  yellowLo: number;
  yellowHi: number;
  redHi: number;
}

export interface FravikBands {
  inlet: FravikBand;
  return: FravikBand;
  delta: FravikBand;
  position: FravikBand;
  flowYellowFrac: number;
  flowRedFrac: number;
}

export type FravikBandKey = "inlet" | "return" | "delta" | "position";

export interface CompanyValve {
  id: number;
  label: string;
  siteId: string;
  deviceId: string;
  buildingId: number | null;
  isBillable: boolean;
  readingCount: number;
  flowLps: number | null;
  lastSyncAt: string | null;
}

export interface CompanyValves {
  valves: CompanyValve[];
  /** A valve whose flow matches the sum of the others — a main pipe in front of its own branches. */
  likelyAggregateValveId: number | null;
}

export interface AtomikaValveBands {
  id: number;
  deviceId: string;
  label: string;
  valveName: string | null;
  isBillable: boolean;
  /** What the label would be with no operator-given name — shown as the input placeholder. */
  derivedLabel: string;
  overrideBands: FravikBands | null;
  effective: FravikBands;
  suggested: FravikBands | null;
}

export interface AtomikaValvesResult {
  siteId: string;
  global: FravikBands;
  valves: AtomikaValveBands[];
}

export interface AtomikaDevicesResult {
  buildings: AtomikaBuilding[];
  customers: AtomikaDeviceCustomer[];
}

export interface VaktSyncStatus {
  running: boolean;
  runningSite: string | null;
  lastReadings: number;
  lastError: string | null;
  lastFinishedAt: string | null;
}

const API_URL: string = import.meta.env.VITE_API_URL ?? "";

export interface Todo {
  id: number;
  title: string;
  isComplete: boolean;
  createdAt: string;
  updatedAt: string | null;
}

/**
 * The scenario `results` jsonb. The frontend owns this shape (see AtomikaScenario.cs); `rows`
 * is carried alongside the summary so a contract generated from the scenario can state the
 * year-by-year split without the backend re-deriving the ESCO formula.
 */
export type ScenarioResults = ProjectionSummary & { rows?: YearRow[] };

export interface Scenario {
  id: number;
  customerName: string;
  notes: string | null;
  inputs: CalcInputs;
  results: ScenarioResults;
  createdAt: string;
  updatedAt: string | null;
}

export interface SaveScenarioPayload {
  customerName: string;
  notes?: string | null;
  inputs: CalcInputs;
  results: ScenarioResults;
}

export interface HealthStatus {
  status: string;
  time: string;
}

const TOKEN_KEY = "atomika-token";

export type UserRole = "superadmin" | "admin" | "user";
export type SalesOrg = "laufid" | "atomika";
export type UserAccessProfile = "custom" | "salesperson";

export interface AuthUser {
  id: string;
  email: string;
  role: UserRole;
  displayName: string | null;
  salesOrg: SalesOrg;
  areaIds: string[] | "*";
  mustChangePassword: boolean;
}

export interface AdminUser {
  id: string;
  email: string;
  displayName: string | null;
  salesOrg: SalesOrg;
  accessProfile: UserAccessProfile;
  role: UserRole;
  isActive: boolean;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  areaIds: string[] | "*";
}

export function getToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearAuth(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export function hasAuth(): boolean {
  return Boolean(getToken());
}

function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const UNAUTHORIZED_EVENT = "atomika:unauthorized";

function handleUnauthorized(path: string): void {
  if (path.startsWith("/api/auth/login")) return;
  clearAuth();
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
  }
}

async function extractError(res: Response, fallback: string): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string };
    if (body?.error) return body.error;
  } catch {
    // response had no JSON body
  }
  return `${fallback} (${res.status})`;
}

async function guard(
  res: Response,
  path: string,
  fallback: string,
): Promise<void> {
  if (res.ok) return;
  if (res.status === 401) handleUnauthorized(path);
  throw new Error(await extractError(res, fallback));
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    headers: { ...authHeaders() },
  });
  await guard(res, path, `Request to ${path} failed`);
  return (await res.json()) as T;
}

async function sendJson<T>(
  path: string,
  method: "POST" | "PATCH" | "PUT",
  body: unknown,
): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
  });
  await guard(res, path, `Request to ${path} failed`);
  return (await res.json()) as T;
}

async function deleteReq(path: string, fallback: string): Promise<void> {
  const res = await fetch(`${API_URL}${path}`, {
    method: "DELETE",
    headers: { ...authHeaders() },
  });
  await guard(res, path, fallback);
}

export interface LeadEstimate {
  arskostnadurKr?: number;
  magnM3?: number;
  vatnsnyting?: number | null;
  savingsOpportunityKr?: number;
  fermetrar?: number;
  lofthaed?: number;
  svaedi?: string;
  postnumer?: string;
}

export interface SubmitLeadPayload {
  name: string;
  email?: string;
  phone?: string;
  message?: string;
  website?: string;
  estimate?: LeadEstimate;
}

function dashboardQuery(params: {
  from?: string;
  to?: string;
  source?: string;
  companyId?: string;
}): string {
  const qs = new URLSearchParams();
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  if (params.source) qs.set("source", params.source);
  if (params.companyId) qs.set("companyId", params.companyId);
  const query = qs.toString();
  return query ? `?${query}` : "";
}

export const api = {
  health: () => getJson<HealthStatus>("/health"),

  submitLead: (payload: SubmitLeadPayload): Promise<{ ok: true }> =>
    sendJson<{ ok: true }>("/api/leads", "POST", payload),

  listTodos: () => getJson<Todo[]>("/api/todos"),

  createTodo: (title: string): Promise<Todo> =>
    sendJson<Todo>("/api/todos", "POST", { title }),

  updateTodo: (
    id: number,
    changes: { title: string; isComplete: boolean },
  ): Promise<Todo> => sendJson<Todo>(`/api/todos/${id}`, "PUT", changes),

  deleteTodo: (id: number): Promise<void> =>
    deleteReq(`/api/todos/${id}`, "Failed to delete todo"),

  listScenarios: () => getJson<Scenario[]>("/api/atomika/scenarios"),

  saveScenario: (payload: SaveScenarioPayload): Promise<Scenario> =>
    sendJson<Scenario>("/api/atomika/scenarios", "POST", payload),

  deleteScenario: (id: number): Promise<void> =>
    deleteReq(`/api/atomika/scenarios/${id}`, "Failed to delete scenario"),

  getSources: (companyId?: string): Promise<DashboardSourcesData> =>
    getJson<DashboardSourcesData>(
      `/api/atomika/sources${companyId ? `?companyId=${encodeURIComponent(companyId)}` : ""}`,
    ),

  getSeries: (
    params: {
      from?: string;
      to?: string;
      source?: string;
      companyId?: string;
    } = {},
  ): Promise<DashboardSeries> =>
    getJson<DashboardSeries>(`/api/atomika/series${dashboardQuery(params)}`),

  getSavings: (
    params: {
      from?: string;
      to?: string;
      source?: string;
      companyId?: string;
    } = {},
  ): Promise<DashboardSavingsData> =>
    getJson<DashboardSavingsData>(
      `/api/atomika/savings${dashboardQuery(params)}`,
    ),

  getDashboardStatus: (companyId?: string): Promise<DashboardStatusData> =>
    getJson<DashboardStatusData>(
      `/api/atomika/status${companyId ? `?companyId=${encodeURIComponent(companyId)}` : ""}`,
    ),

  getCustomers: (): Promise<CustomerListResult> =>
    getJson<CustomerListResult>("/api/atomika/customers"),

  getFleet: (): Promise<FleetData> => getJson<FleetData>("/api/atomika/fleet"),

  getShowcase: (): Promise<ShowcaseEntry[]> =>
    getJson<ShowcaseEntry[]>("/api/atomika/showcase"),

  getShowcaseTech: (id: string): Promise<ShowcaseTech> =>
    getJson<ShowcaseTech>(
      `/api/atomika/showcase/${encodeURIComponent(id)}/tech`,
    ),

  getFleetSummary: (months = 12): Promise<FleetSummary> =>
    getJson<FleetSummary>(`/api/atomika/fleet/summary?months=${months}`),

  getAtomikaDevices: (): Promise<AtomikaDevicesResult> =>
    getJson<AtomikaDevicesResult>("/api/atomika/devices"),

  linkAtomikaDevice: (
    siteId: string,
    kennitala: string | null,
  ): Promise<{ siteId: string; kennitala: string | null }> =>
    sendJson(`/api/atomika/devices/${encodeURIComponent(siteId)}/link`, "PUT", {
      kennitala,
    }),

  getAtomikaValveBands: (siteId: string): Promise<AtomikaValvesResult> =>
    getJson<AtomikaValvesResult>(
      `/api/atomika/devices/${encodeURIComponent(siteId)}/valves`,
    ),

  setAtomikaValveBands: (
    siteId: string,
    valveId: number,
    bands: FravikBands | null,
  ): Promise<{ id: number; overrideBands: FravikBands | null }> =>
    sendJson(
      `/api/atomika/devices/${encodeURIComponent(siteId)}/valves/${valveId}/bands`,
      "PUT",
      { bands },
    ),

  setAtomikaValveName: (
    siteId: string,
    valveId: number,
    name: string | null,
  ): Promise<{ id: number; valveName: string | null; label: string }> =>
    sendJson(
      `/api/atomika/devices/${encodeURIComponent(siteId)}/valves/${valveId}/name`,
      "PUT",
      { name },
    ),

  getCompanyValves: (kennitala: string): Promise<CompanyValves> =>
    getJson<CompanyValves>(
      `/api/markadur/companies/${encodeURIComponent(kennitala)}/valves`,
    ),

  saveBuildingValves: (
    buildingId: number,
    payload: { valveIds: number[]; billableIds: number[] },
  ): Promise<CompanyValves> =>
    sendJson<CompanyValves>(
      `/api/markadur/buildings/${buildingId}/valves`,
      "PUT",
      payload,
    ),

  syncVaktNow: (): Promise<{ started: boolean }> =>
    sendJson<{ started: boolean }>("/api/atomika/devices/sync", "POST", {}),

  backfillDevice: (
    siteId: string,
    backfillDays: number,
  ): Promise<{ started: boolean }> =>
    sendJson<{ started: boolean }>(
      `/api/atomika/devices/${encodeURIComponent(siteId)}/sync?backfillDays=${backfillDays}`,
      "POST",
      {},
    ),

  getVaktSyncStatus: (): Promise<VaktSyncStatus> =>
    getJson<VaktSyncStatus>("/api/atomika/devices/sync/status"),

  setCustomerLocation: (
    kennitala: string,
    lat: number | null,
    lng: number | null,
  ): Promise<{ kennitala: string; lat: number | null; lng: number | null }> =>
    sendJson(
      `/api/markadur/customers/${encodeURIComponent(kennitala)}/location`,
      "PUT",
      { lat, lng },
    ),

  getOverview: (): Promise<OverviewData> =>
    getJson<OverviewData>("/api/atomika/overview"),

  getMarkadurStages: (): Promise<StagesConfig> =>
    getJson<StagesConfig>("/api/markadur/stages"),

  searchCompanies: (q: string, limit = 25): Promise<CompanySearchResult> => {
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    qs.set("limit", String(limit));
    return getJson<CompanySearchResult>(`/api/markadur/companies?${qs}`);
  },

  registerCompany: (payload: RegisterCompanyPayload): Promise<Company> =>
    sendJson<Company>("/api/markadur/companies", "POST", payload),

  getCompany: (kennitala: string): Promise<CompanyDetail> =>
    getJson<CompanyDetail>(
      `/api/markadur/companies/${encodeURIComponent(kennitala)}`,
    ),

  createCompanyBuilding: (
    kennitala: string,
    name?: string,
  ): Promise<CompanyBuilding> =>
    sendJson<CompanyBuilding>(
      `/api/markadur/companies/${encodeURIComponent(kennitala)}/buildings`,
      "POST",
      { name },
    ),

  saveCompanyBuilding: (
    kennitala: string,
    id: number,
    payload: SaveBuildingPayload,
  ): Promise<SaveBuildingResult> =>
    sendJson<SaveBuildingResult>(
      `/api/markadur/companies/${encodeURIComponent(kennitala)}/buildings/${id}`,
      "PUT",
      payload,
    ),

  deleteCompanyBuilding: (kennitala: string, id: number): Promise<void> =>
    deleteReq(
      `/api/markadur/companies/${encodeURIComponent(kennitala)}/buildings/${id}`,
      "Mistókst að eyða húsnæði",
    ),

  listServiceTerms: (buildingId: number): Promise<ServiceTermsList> =>
    getJson<ServiceTermsList>(
      `/api/markadur/buildings/${buildingId}/service-terms`,
    ),

  prefillServiceTerms: (buildingId: number): Promise<ServiceTermsBaseline> =>
    getJson<ServiceTermsBaseline>(
      `/api/markadur/buildings/${buildingId}/service-terms/from-invoices`,
    ),

  createServiceTerms: (
    buildingId: number,
    payload: {
      effectiveFrom: string;
      kind: ServiceTermsKind;
      reason: string;
      baseline: ServiceTermsBaseline;
      signedDate?: string | null;
      termStart?: string | null;
      termLengthMonths?: number | null;
      feePercentage?: number | null;
      monthlyFeeKr?: number | null;
      feeIndexBasis?: string | null;
      feeIndexBaseValue?: number | null;
      feeIndexBaseMonth?: string | null;
      installationCostKr?: number | null;
      billingPeriod?: string;
      priceSourceKind?: string;
      paymentOption?: "fyrirfram" | "fjarmagnad";
      minimumCommitmentMonths?: number | null;
      expectedSavingPct?: number | null;
    },
  ): Promise<ServiceTermsVersion> =>
    sendJson<ServiceTermsVersion>(
      `/api/markadur/buildings/${buildingId}/service-terms`,
      "POST",
      payload,
    ),

  deleteServiceTerms: (buildingId: number, versionId: number): Promise<void> =>
    deleteReq(
      `/api/markadur/buildings/${buildingId}/service-terms/${versionId}`,
      "Mistókst að eyða útgáfu",
    ),

  getInvoices: (kennitala: string): Promise<CustomerInvoices> =>
    getJson<CustomerInvoices>(
      `/api/markadur/companies/${encodeURIComponent(kennitala)}/invoices`,
    ),

  saveInvoices: (
    kennitala: string,
    atomikaSettingsId: number,
    months: InvoiceMonthValue[],
  ): Promise<InvoiceMonthValue[]> =>
    sendJson<InvoiceMonthValue[]>(
      `/api/markadur/companies/${encodeURIComponent(kennitala)}/invoices/${atomikaSettingsId}`,
      "PUT",
      { months },
    ),

  getWaterTariffs: (kennitala: string): Promise<WaterTariffs> =>
    getJson<WaterTariffs>(
      `/api/markadur/companies/${encodeURIComponent(kennitala)}/water-tariff`,
    ),

  saveWaterTariff: (
    kennitala: string,
    payload: {
      pricePerM3VatKr: number;
      effectiveFrom: string;
      note?: string;
      createdBy?: string;
      unit?: string;
      buildingId?: number | null;
    },
  ): Promise<WaterTariffs> =>
    sendJson<WaterTariffs>(
      `/api/markadur/companies/${encodeURIComponent(kennitala)}/water-tariff`,
      "PUT",
      payload,
    ),

  getBuildingWaterTariffs: (buildingId: number): Promise<WaterTariffs> =>
    getJson<WaterTariffs>(`/api/markadur/buildings/${buildingId}/water-tariff`),

  saveBuildingWaterTariff: (
    buildingId: number,
    payload: {
      pricePerM3VatKr: number;
      effectiveFrom: string;
      note?: string;
      createdBy?: string;
      unit?: string;
    },
  ): Promise<WaterTariffs> =>
    sendJson<WaterTariffs>(
      `/api/markadur/buildings/${buildingId}/water-tariff`,
      "PUT",
      payload,
    ),

  deleteWaterTariff: (kennitala: string, id: number): Promise<void> =>
    deleteReq(
      `/api/markadur/companies/${encodeURIComponent(kennitala)}/water-tariff/${id}`,
      "Mistókst að eyða verðskrá",
    ),

  getExpectedSaving: (kennitala: string): Promise<ExpectedSaving> =>
    getJson<ExpectedSaving>(
      `/api/markadur/companies/${encodeURIComponent(kennitala)}/expected-saving`,
    ),

  saveExpectedSaving: (
    kennitala: string,
    payload: {
      expectedSavingPct: number | null;
      note?: string | null;
      updatedBy?: string;
    },
  ): Promise<ExpectedSaving> =>
    sendJson<ExpectedSaving>(
      `/api/markadur/companies/${encodeURIComponent(kennitala)}/expected-saving`,
      "PUT",
      payload,
    ),

  getSavingsShare: (kennitala: string): Promise<SavingsShare> =>
    getJson<SavingsShare>(
      `/api/markadur/companies/${encodeURIComponent(kennitala)}/savings-share`,
    ),

  saveSavingsShare: (
    kennitala: string,
    payload: { atomikaSharePct: number; updatedBy?: string },
  ): Promise<SavingsShare> =>
    sendJson<SavingsShare>(
      `/api/markadur/companies/${encodeURIComponent(kennitala)}/savings-share`,
      "PUT",
      payload,
    ),

  getPipeline: (org?: string): Promise<Pipeline> =>
    getJson<Pipeline>(
      `/api/markadur/pipeline${org ? `?org=${encodeURIComponent(org)}` : ""}`,
    ),

  createOpportunity: (
    payload: CreateOpportunityPayload,
  ): Promise<Opportunity> =>
    sendJson<Opportunity>("/api/markadur/opportunities", "POST", payload),

  updateOpportunity: (
    id: number,
    payload: UpdateOpportunityPayload,
  ): Promise<Opportunity> =>
    sendJson<Opportunity>(
      `/api/markadur/opportunities/${id}`,
      "PATCH",
      payload,
    ),

  deleteOpportunity: (id: number): Promise<void> =>
    deleteReq(
      `/api/markadur/opportunities/${id}`,
      "Failed to delete opportunity",
    ),

  getLaufidStatus: (): Promise<LaufidStatus> =>
    getJson<LaufidStatus>("/api/laufid/status"),

  getLaufidLinks: (kennitala: string): Promise<LaufidLink[]> =>
    getJson<LaufidLink[]>(`/api/laufid/links/${encodeURIComponent(kennitala)}`),

  createLaufidCustomer: (
    payload: CreateLaufidCustomerPayload,
  ): Promise<LaufidCustomerResult> =>
    sendJson<LaufidCustomerResult>("/api/laufid/customers", "POST", payload),

  listContracts: (kennitala?: string): Promise<Contract[]> =>
    getJson<Contract[]>(
      `/api/markadur/contracts${kennitala ? `?kennitala=${encodeURIComponent(kennitala)}` : ""}`,
    ),

  createContract: (payload: CreateContractPayload): Promise<Contract> =>
    sendJson<Contract>("/api/markadur/contracts", "POST", payload),

  updateContract: (
    id: number,
    payload: UpdateContractPayload,
  ): Promise<Contract> =>
    sendJson<Contract>(`/api/markadur/contracts/${id}`, "PATCH", payload),

  regenerateContract: (id: number): Promise<Contract> =>
    sendJson<Contract>(`/api/markadur/contracts/${id}/endurgera`, "POST", {}),

  deleteContract: (id: number, reason?: string): Promise<void> =>
    deleteReq(
      `/api/markadur/contracts/${id}${reason ? `?reason=${encodeURIComponent(reason)}` : ""}`,
      "Failed to delete contract",
    ),

  listDocuments: (kennitala: string): Promise<CrmDocument[]> =>
    getJson<CrmDocument[]>(
      `/api/markadur/documents?kennitala=${encodeURIComponent(kennitala)}`,
    ),

  uploadDocument: async (params: {
    file: File;
    kennitala: string;
    kind: DocumentKind;
    opportunityId?: number | null;
    contractId?: number | null;
  }): Promise<CrmDocument> => {
    const qs = new URLSearchParams({
      kennitala: params.kennitala,
      kind: params.kind,
    });
    if (params.opportunityId)
      qs.set("opportunityId", String(params.opportunityId));
    if (params.contractId) qs.set("contractId", String(params.contractId));

    const body = new FormData();
    body.append("file", params.file);

    const path = `/api/markadur/documents?${qs}`;
    const res = await fetch(`${API_URL}${path}`, {
      method: "POST",
      headers: { ...authHeaders() },
      body,
    });
    await guard(res, path, "Upload failed");
    return (await res.json()) as CrmDocument;
  },

  fetchDocument: async (id: number): Promise<Blob> => {
    const path = `/api/markadur/documents/${id}/efni`;
    const res = await fetch(`${API_URL}${path}`, {
      headers: { ...authHeaders() },
    });
    await guard(res, path, "Download failed");
    return res.blob();
  },

  deleteDocument: (id: number): Promise<void> =>
    deleteReq(`/api/markadur/documents/${id}`, "Failed to delete document"),

  createContact: (payload: CreateContactPayload): Promise<Contact> =>
    sendJson<Contact>("/api/markadur/contacts", "POST", payload),

  updateContact: (
    id: number,
    payload: UpdateContactPayload,
  ): Promise<Contact> =>
    sendJson<Contact>(`/api/markadur/contacts/${id}`, "PATCH", payload),

  deleteContact: (id: number): Promise<void> =>
    deleteReq(`/api/markadur/contacts/${id}`, "Failed to delete contact"),

  createActivity: (payload: CreateActivityPayload): Promise<Activity> =>
    sendJson<Activity>("/api/markadur/activities", "POST", payload),

  getMarkadurCustomers: (
    activeOnly = false,
  ): Promise<MarkadurCustomerListResult> =>
    getJson<MarkadurCustomerListResult>(
      `/api/markadur/customers${activeOnly ? "?activeOnly=true" : ""}`,
    ),

  activateCustomer: (
    kennitala: string,
    payload: ActivateCustomerPayload = {},
  ): Promise<{ activated: boolean; kennitala: string }> =>
    sendJson(
      `/api/markadur/customers/${encodeURIComponent(kennitala)}/activate`,
      "POST",
      payload,
    ),

  deactivateCustomer: (kennitala: string): Promise<void> =>
    deleteReq(
      `/api/markadur/customers/${encodeURIComponent(kennitala)}/activate`,
      "Failed to deactivate customer",
    ),

  listApiKeys: (): Promise<ApiKeySummary[]> => getJson("/api/api-keys"),

  createApiKey: (payload: CreateApiKeyPayload): Promise<ApiKeyCreated> =>
    sendJson("/api/api-keys", "POST", payload),

  revokeApiKey: (id: number): Promise<ApiKeySummary> =>
    sendJson(`/api/api-keys/${id}/revoke`, "POST", {}),

  deleteApiKey: (id: number): Promise<void> =>
    deleteReq(`/api/api-keys/${id}`, "Failed to delete API key"),

  login: (
    email: string,
    password: string,
  ): Promise<{ token: string; user: AuthUser }> =>
    sendJson<{ token: string; user: AuthUser }>("/api/auth/login", "POST", {
      email,
      password,
    }),

  logout: (): Promise<{ ok: true }> =>
    sendJson<{ ok: true }>("/api/auth/logout", "POST", {}),

  forgotPassword: (email: string): Promise<{ ok: true }> =>
    sendJson<{ ok: true }>("/api/auth/forgot-password", "POST", { email }),

  me: (): Promise<{ user: AuthUser }> =>
    getJson<{ user: AuthUser }>("/api/auth/me"),

  changePassword: (
    currentPassword: string,
    newPassword: string,
  ): Promise<{ ok: true; token: string }> =>
    sendJson<{ ok: true; token: string }>("/api/auth/change-password", "POST", {
      currentPassword,
      newPassword,
    }),

  admin: {
    listUsers: (): Promise<{ users: AdminUser[] }> =>
      getJson<{ users: AdminUser[] }>("/api/admin/users"),

    createUser: (body: {
      email: string;
      displayName?: string;
      password: string;
      role: UserRole;
      salesOrg: SalesOrg;
      accessProfile: UserAccessProfile;
      areaIds?: string[];
    }): Promise<{ id: string }> =>
      sendJson<{ id: string }>("/api/admin/users", "POST", body),

    updateUser: (
      id: string,
      body: {
        displayName?: string;
        isActive?: boolean;
        role?: UserRole;
        salesOrg?: SalesOrg;
        accessProfile?: UserAccessProfile;
        password?: string;
      },
    ): Promise<{ ok: true }> =>
      sendJson<{ ok: true }>(`/api/admin/users/${id}`, "PATCH", body),

    setAccess: (id: string, areaIds: string[]): Promise<{ ok: true }> =>
      sendJson<{ ok: true }>(`/api/admin/users/${id}/access`, "PUT", {
        areaIds,
      }),

    sendCredentials: (
      id: string,
    ): Promise<{
      email: string;
      tempPassword: string;
      loginUrl: string;
      emailSent: boolean;
    }> => sendJson(`/api/admin/users/${id}/send-credentials`, "POST", {}),

    deleteUser: (id: string): Promise<void> =>
      deleteReq(`/api/admin/users/${id}`, "Failed to delete user"),

    getSettings: (): Promise<AdminSettings> =>
      getJson<AdminSettings>("/api/admin/settings"),

    updateSettings: (body: UpdateSettingsPayload): Promise<AdminSettings> =>
      sendJson<AdminSettings>("/api/admin/settings", "PUT", body),

    testVakt: (body: {
      baseUrl?: string;
      auth?: VaktAuth;
      username?: string;
      password?: string;
      token?: string;
      keyMap?: Record<string, string>;
    }): Promise<VaktTestResult> =>
      sendJson<VaktTestResult>("/api/admin/settings/vakt/test", "POST", body),

    mailpitSession: async (): Promise<boolean> => {
      try {
        const res = await fetch(`${API_URL}/api/admin/mailpit/session`, {
          method: "POST",
          headers: { ...authHeaders() },
        });
        return res.ok;
      } catch {
        return false;
      }
    },
  },

  listDutyPeople: (): Promise<DutyPerson[]> =>
    getJson<DutyPerson[]>("/api/vaktaskra/people"),

  listDutyNow: (): Promise<DutyShift[]> =>
    getJson<DutyShift[]>("/api/vaktaskra/shifts/now"),

  listDutyShifts: (from: string, to: string): Promise<DutyShift[]> =>
    getJson<DutyShift[]>(
      `/api/vaktaskra/shifts?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    ),

  createDutyShift: (body: {
    userId: string;
    startsAt: string;
    endsAt: string;
    note?: string | null;
  }): Promise<DutyShift> =>
    sendJson<DutyShift>("/api/vaktaskra/shifts", "POST", body),

  updateDutyShift: (
    id: number,
    body: {
      userId?: string;
      startsAt?: string;
      endsAt?: string;
      note?: string | null;
    },
  ): Promise<DutyShift> =>
    sendJson<DutyShift>(`/api/vaktaskra/shifts/${id}`, "PATCH", body),

  deleteDutyShift: (id: number): Promise<void> =>
    deleteReq(`/api/vaktaskra/shifts/${id}`, "Failed to delete shift"),
};
